/**
 * Footer Component
 * Site footer with company info, link sections, payment methods, and social links
 * Based on Webshop-frontend Footer structure
 */

import type {
  Footer as FooterType,
  LocalizedTexts,
  UmbracoLink,
  LinkListWithHeading,
} from "@/types/layout";
import type { LocaleOptions } from "@/api/locale";
import {
  FacebookIcon,
  InstagramIcon,
  YouTubeIcon,
  GlobeIcon,
  ChevronDownIcon,
} from "@/components/icons";
import { t } from "@/utils/translations";
import styles from "./Footer.module.css";
import { resolveHref } from "@/utils/url";
import { resolveLocaleDisplay } from "@/utils/locale-display";

interface FooterProps {
  footer: FooterType;
  localizedTexts: LocalizedTexts;
  currentLocale?: string;
  currentCountry?: string;
  localeOptions?: LocaleOptions;
  onLocationSelectorClick?: () => void;
}

// Payment methods configuration
const PAYMENT_METHODS = [
  { name: "Klarna", logo: "/images/partners/klarna.svg" },
  { name: "Mastercard", logo: "/images/partners/master-card.svg" },
  { name: "Maestro", logo: "/images/partners/master-card-maestro.svg" },
  { name: "Visa", logo: "/images/partners/visa.svg" },
  { name: "Visa Electron", logo: "/images/partners/visa-electron.svg" },
];

// Link Column Component
interface LinkColumnProps {
  section: LinkListWithHeading;
  currentLocale: string;
}

function LinkColumn({ section, currentLocale }: LinkColumnProps) {
  return (
    <div className={styles.linkColumn}>
      <h3 className={styles.columnTitle}>{section.heading}</h3>
      <ul className={styles.linkList}>
        {section.links?.map((link, index) => (
          <li key={`${link.id}-${index}`}>
            <a
              href={resolveHref(link, currentLocale)}
              className={styles.footerLink}
              target={link.target === "_blank" ? "_blank" : undefined}
              rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
            >
              {link.name}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Payment Methods Component
interface PaymentMethodsProps {
  label: string;
  isMobile?: boolean;
}

function PaymentMethods({ label, isMobile }: PaymentMethodsProps) {
  return (
    <div>
      <span
        className={isMobile ? styles.mobilePaymentLabel : styles.sectionLabel}
      >
        {label}
      </span>
      <div className={styles.paymentLogosWrapper}>
        {PAYMENT_METHODS.map((method) => (
          <img
            key={method.name}
            src={method.logo}
            alt={method.name}
            className={styles.paymentLogo}
          />
        ))}
      </div>
    </div>
  );
}

// Social Links Component
interface SocialLinksProps {
  facebookLink?: UmbracoLink | null;
  instagramLink?: UmbracoLink | null;
  youTubeLink?: UmbracoLink | null;
  label: string;
}

function SocialLinks({
  facebookLink,
  instagramLink,
  youTubeLink,
  label,
}: SocialLinksProps) {
  const hasSocialLinks = facebookLink || instagramLink || youTubeLink;
  if (!hasSocialLinks) return null;

  return (
    <div className={styles.socialLinksWrapper}>
      <span className={styles.socialLabel}>{label}</span>
      <div className={styles.socialIconsWrapper}>
        {facebookLink && (
          <a
            href={facebookLink.url}
            className={styles.socialLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={facebookLink.name || "Facebook"}
          >
            <FacebookIcon className={styles.socialIcon} />
          </a>
        )}
        {instagramLink && (
          <a
            href={instagramLink.url}
            className={styles.socialLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={instagramLink.name || "Instagram"}
          >
            <InstagramIcon className={styles.socialIcon} />
          </a>
        )}
        {youTubeLink && (
          <a
            href={youTubeLink.url}
            className={styles.socialLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={youTubeLink.name || "YouTube"}
          >
            <YouTubeIcon className={styles.socialIcon} />
          </a>
        )}
      </div>
    </div>
  );
}

// Location Selector Component
interface LocationSelectorProps {
  label: string;
  globalText: string;
  flagSrc?: string | null;
  onClick?: () => void;
}

function LocationSelector({
  label,
  globalText,
  flagSrc,
  onClick,
}: LocationSelectorProps) {
  return (
    <div className={styles.locationSelectorWrapper}>
      <p className={styles.locationLabel}>{label}</p>
      <button type="button" className={styles.regionSelector} onClick={onClick}>
        {flagSrc ? (
          <img
            src={flagSrc}
            alt=""
            width={20}
            height={20}
            style={{ flexShrink: 0, borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          <GlobeIcon className={styles.globeIcon} />
        )}
        <span>{globalText}</span>
        <ChevronDownIcon className={styles.chevronIcon} />
      </button>
    </div>
  );
}

// Company Info Component
interface CompanyInfoProps {
  addressLine1?: string;
  addressLine2?: string;
  address?: string;
  websiteLink?: UmbracoLink | null;
  copyrightText?: string;
  isDesktop?: boolean;
}

function CompanyInfo({
  addressLine1,
  addressLine2,
  address,
  websiteLink,
  copyrightText,
  isDesktop,
}: CompanyInfoProps) {
  return (
    <div
      className={
        isDesktop ? styles.desktopCompanyInfoWrapper : styles.companyInfoWrapper
      }
    >
      <address className={styles.companyAddress}>
        {addressLine1 && <>{addressLine1}</>}
        {isDesktop && addressLine2 && (
          <>
            <br />
            {addressLine2}
          </>
        )}
        {!isDesktop && addressLine2 && (
          <>
            {" "}
            <span className={styles.companyLocation}>{addressLine2}</span>
          </>
        )}
        {!addressLine1 && !addressLine2 && address && <>{address}</>}
      </address>
      {websiteLink && (
        <a
          href={websiteLink.url}
          className={styles.companyLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {websiteLink.name}
        </a>
      )}
      {copyrightText && (
        <span className={styles.copyright}>{copyrightText}</span>
      )}
    </div>
  );
}

// Desktop Footer Layout
interface FooterLayoutProps {
  links?: LinkListWithHeading[];
  currentLocale: string;
  addressLine1?: string;
  addressLine2?: string;
  address?: string;
  websiteLink?: UmbracoLink | null;
  copyrightText?: string;
  facebookLink?: UmbracoLink | null;
  instagramLink?: UmbracoLink | null;
  youTubeLink?: UmbracoLink | null;
  paymentLabel: string;
  socialLabel: string;
  locationLabel: string;
  locationDisplayText: string;
  locationFlagSrc?: string | null;
  onLocationSelectorClick?: () => void;
}

function DesktopFooter({
  links,
  currentLocale,
  addressLine1,
  addressLine2,
  address,
  websiteLink,
  copyrightText,
  facebookLink,
  instagramLink,
  youTubeLink,
  paymentLabel,
  socialLabel,
  locationLabel,
  locationDisplayText,
  locationFlagSrc,
  onLocationSelectorClick,
}: FooterLayoutProps) {
  return (
    <div className={styles.desktopWrapper}>
      <div className={styles.desktopColumns}>
        {/* Left side: Logo + Link Columns + Company Info */}
        <div className={styles.leftColumn}>
          <div className={styles.linksColumnsWrapper}>
            <img
              src="/images/wehunt-logo-light-green.png"
              alt="WeHunt"
              className={`${styles.logo} ${styles.logoDark}`}
            />
            <img
              src="/images/wehunt-logo-darkgreen.png"
              alt=""
              aria-hidden="true"
              className={`${styles.logo} ${styles.logoLight}`}
            />
            {links?.map((section) => (
              <LinkColumn
                key={section.id}
                section={section}
                currentLocale={currentLocale}
              />
            ))}
          </div>
          <CompanyInfo
            addressLine1={addressLine1}
            addressLine2={addressLine2}
            address={address}
            websiteLink={websiteLink}
            copyrightText={copyrightText}
            isDesktop
          />
        </div>

        {/* Right side: Payment Methods, Follow Us, Location Selector */}
        <div className={styles.rightColumn}>
          <PaymentMethods label={paymentLabel} />
          <SocialLinks
            facebookLink={facebookLink}
            instagramLink={instagramLink}
            youTubeLink={youTubeLink}
            label={socialLabel}
          />
          <LocationSelector
            label={locationLabel}
            globalText={locationDisplayText}
            flagSrc={locationFlagSrc}
            onClick={onLocationSelectorClick}
          />
        </div>
      </div>
    </div>
  );
}

// Mobile Footer Layout
function MobileFooter({
  links,
  currentLocale,
  addressLine1,
  addressLine2,
  address,
  websiteLink,
  copyrightText,
  facebookLink,
  instagramLink,
  youTubeLink,
  paymentLabel,
  socialLabel,
  locationLabel,
  locationDisplayText,
  locationFlagSrc,
  onLocationSelectorClick,
}: FooterLayoutProps) {
  return (
    <div className={styles.mobileWrapper}>
      {/* Location Selector at top on mobile */}
      <LocationSelector
        label={locationLabel}
        globalText={locationDisplayText}
        flagSrc={locationFlagSrc}
        onClick={onLocationSelectorClick}
      />

      {/* Accordion Sections */}
      <div className={styles.mobileAccordion}>
        {links?.map((section) => (
          <details key={section.id} className={styles.accordionItem}>
            <summary className={styles.accordionControl}>
              <span>{section.heading}</span>
              <ChevronDownIcon className={styles.accordionChevron} />
            </summary>
            <div className={styles.accordionPanel}>
              <ul className={styles.linkList}>
                {section.links?.map((link, index) => (
                  <li key={`${link.id}-${index}`}>
                    <a
                      href={resolveHref(link, currentLocale)}
                      className={styles.footerLink}
                      target={link.target === "_blank" ? "_blank" : undefined}
                      rel={
                        link.target === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ))}
      </div>

      {/* Social Links */}
      <SocialLinks
        facebookLink={facebookLink}
        instagramLink={instagramLink}
        youTubeLink={youTubeLink}
        label={socialLabel}
      />

      {/* Payment Methods */}
      <PaymentMethods label={paymentLabel} isMobile />

      {/* Company Info */}
      <CompanyInfo
        addressLine1={addressLine1}
        addressLine2={addressLine2}
        address={address}
        websiteLink={websiteLink}
        copyrightText={copyrightText}
      />
    </div>
  );
}

export function Footer({
  footer,
  localizedTexts,
  currentLocale = "sv-SE",
  currentCountry,
  localeOptions,
  onLocationSelectorClick,
}: FooterProps) {
  const paymentLabel = t(
    localizedTexts,
    "Tracker.Generic.Footer.PaymentMethodsLabel",
    "Payment methods"
  );
  const socialLabel = t(
    localizedTexts,
    "Tracker.Generic.Footer.SocialLabel",
    "Follow Us"
  );
  const locationLabel = t(
    localizedTexts,
    "Tracker.Generic.Footer.RegionLabel",
    "You are currently browsing our website in:"
  );
  const globalText = t(
    localizedTexts,
    "Tracker.Generic.Footer.Global",
    "Global"
  );

  // Show current country name + flag instead of "Global"
  const { label: locationDisplayText, flagSrc: locationFlagSrc } =
    resolveLocaleDisplay({
      currentLocale,
      currentCountry,
      localeOptions,
      fallbackLabel: globalText,
    });

  const footerLayoutProps: FooterLayoutProps = {
    links: footer.links,
    currentLocale,
    addressLine1: footer.addressLine1,
    addressLine2: footer.addressLine2,
    address: footer.address,
    websiteLink: footer.websiteLink,
    copyrightText: footer.copyrightText,
    facebookLink: footer.facebookLink,
    instagramLink: footer.instagramLink,
    youTubeLink: footer.youTubeLink,
    paymentLabel,
    socialLabel,
    locationLabel,
    locationDisplayText,
    locationFlagSrc,
    onLocationSelectorClick,
  };

  return (
    <footer className={styles.footer} role="contentinfo">
      <div className={`container ${styles.footerContainer}`}>
        {/* Desktop Footer - hidden on mobile */}
        <div className={styles.desktopOnly}>
          <DesktopFooter {...footerLayoutProps} />
        </div>
        {/* Mobile Footer - hidden on desktop */}
        <div className={styles.mobileOnly}>
          <MobileFooter {...footerLayoutProps} />
        </div>
      </div>
    </footer>
  );
}

export default Footer;
